import apiClient from './client';

export interface GlobalDomain {
  id: string;
  name: string;
  slug: string;
  description?: string;
  icon?: string;
  sortOrder?: number;
  categoryCount?: number;
  createdAt: string;
  updatedAt: string;
}

export interface DomainListResponse {
  domains: GlobalDomain[];
  total: number;
}

export interface GlobalCategory {
  id: string;
  name: string;
  slug: string;
  description?: string;
  icon?: string;
  domainId?: string;
  domainName?: string;
  domainSlug?: string;
  documentCount?: number;
  createdAt: string;
  updatedAt: string;
}

export interface GlobalDocument {
  id: string;
  categoryId: string;
  filename: string;
  originalFilename: string;
  fileType: string;
  mimeType: string;
  fileSizeBytes: number;
  s3Key: string;
  status: 'pending' | 'uploaded' | 'indexing' | 'indexed' | 'failed';
  indexedAt?: string;
  error?: string;
  mainTopic?: string;
  subTopics?: string[];
  chunkCount?: number;
  // ATO metadata fields
  sourceUrl?: string;
  publishDate?: string;
  lastUpdatedDate?: string;
  documentCategory?: string;
  atoSection?: string;
  contentText?: string;
  wordCount?: number;
  hasTables?: boolean;
  tableCount?: number;
  categoryName?: string;
  categorySlug?: string;
  createdAt: string;
  updatedAt: string;
}

export interface CategoryListResponse {
  categories: GlobalCategory[];
  total: number;
}

export interface DocumentListResponse {
  documents: GlobalDocument[];
  total: number;
  limit: number;
  offset: number;
}

export interface ATOUploadResult {
  documentId: string;
  filename: string;
  status: string;
  documentCategory: string;
  categoryName: string;
  mainTopic: string;
  chunkCount: number;
  wordCount: number;
  hasTables: boolean;
  tableCount: number;
  sourceUrl?: string;
}

export interface UploadUrlResponse {
  uploadUrl: string;
  documentId: string;
  s3Key: string;
  expiresIn: number;
}

export interface ListDocumentsParams {
  categoryId?: string;
  categorySlug?: string;
  documentCategory?: string;
  /** Restrict to one knowledge domain (e.g. "fair-work", "ato", "asic"). */
  domainSlug?: string;
  limit?: number;
  offset?: number;
  status?: string;
  search?: string;
  sortBy?: 'createdAt' | 'filename' | 'fileSizeBytes' | 'publishDate' | 'lastUpdatedDate' | 'title' | 'documentCategory';
  sortOrder?: 'asc' | 'desc';
}

class GlobalKnowledgeService {
  // Domain endpoints
  async listDomains(): Promise<DomainListResponse> {
    const response = await apiClient.get('/global-knowledge/domains');
    return response.data.data;
  }

  async createDomain(data: {
    name: string;
    slug: string;
    description?: string;
    icon?: string;
  }): Promise<GlobalDomain> {
    const response = await apiClient.post('/global-knowledge/domains', data);
    return response.data.data;
  }

  async updateDomain(
    id: string,
    data: { name?: string; description?: string; icon?: string; sortOrder?: number }
  ): Promise<GlobalDomain> {
    const response = await apiClient.put(`/global-knowledge/domains/${id}`, data);
    return response.data.data;
  }

  async deleteDomain(id: string): Promise<void> {
    await apiClient.delete(`/global-knowledge/domains/${id}`);
  }

  // Category endpoints
  async listCategories(opts: { domainSlug?: string } = {}): Promise<CategoryListResponse> {
    const params = opts.domainSlug ? { domainSlug: opts.domainSlug } : undefined;
    const response = await apiClient.get('/global-knowledge/categories', { params });
    return response.data.data;
  }

  async getCategory(id: string): Promise<GlobalCategory> {
    const response = await apiClient.get(`/global-knowledge/categories/${id}`);
    return response.data.data;
  }

  async createCategory(data: {
    name: string;
    slug: string;
    description?: string;
    icon?: string;
    domainId?: string;
  }): Promise<GlobalCategory> {
    const response = await apiClient.post('/global-knowledge/categories', data);
    return response.data.data;
  }

  async updateCategory(
    id: string,
    data: { name?: string; description?: string; icon?: string; domainId?: string }
  ): Promise<GlobalCategory> {
    const response = await apiClient.put(`/global-knowledge/categories/${id}`, data);
    return response.data.data;
  }

  async deleteCategory(id: string): Promise<void> {
    await apiClient.delete(`/global-knowledge/categories/${id}`);
  }

  // Document endpoints
  async listDocuments(params: ListDocumentsParams = {}): Promise<DocumentListResponse> {
    const queryParams = new URLSearchParams();
    if (params.categoryId) queryParams.append('categoryId', params.categoryId);
    if (params.categorySlug) queryParams.append('categorySlug', params.categorySlug);
    if (params.documentCategory) queryParams.append('documentCategory', params.documentCategory);
    if (params.domainSlug) queryParams.append('domainSlug', params.domainSlug);
    if (params.limit) queryParams.append('limit', params.limit.toString());
    if (params.offset) queryParams.append('offset', params.offset.toString());
    if (params.status) queryParams.append('status', params.status);
    if (params.search) queryParams.append('search', params.search);
    if (params.sortBy) queryParams.append('sortBy', params.sortBy);
    if (params.sortOrder) queryParams.append('sortOrder', params.sortOrder);

    const response = await apiClient.get(`/global-knowledge/documents?${queryParams.toString()}`);
    return response.data.data;
  }

  async getDocument(id: string): Promise<GlobalDocument> {
    const response = await apiClient.get(`/global-knowledge/documents/${id}`);
    return response.data.data;
  }

  async getDocumentContent(id: string): Promise<{ contentText: string; filename: string }> {
    const response = await apiClient.get(`/global-knowledge/documents/${id}/content`);
    return response.data.data;
  }

  async getDocumentChunks(id: string): Promise<{
    documentId: string;
    filename: string;
    mainTopic?: string;
    totalChunks: number;
    chunks: Array<{ index: number; text: string; tokens?: number }>;
  }> {
    const response = await apiClient.get(`/global-knowledge/documents/${id}/chunks`);
    return response.data.data;
  }

  async getUploadUrl(
    categoryId: string,
    filename: string,
    contentType: string,
    fileSizeBytes: number
  ): Promise<UploadUrlResponse> {
    const response = await apiClient.post('/global-knowledge/documents/upload-url', {
      categoryId,
      filename,
      contentType,
      fileSizeBytes,
    });
    return response.data.data;
  }

  async uploadFile(uploadUrl: string, file: File, contentType?: string): Promise<void> {
    await fetch(uploadUrl, {
      method: 'PUT',
      body: file,
      headers: {
        'Content-Type': contentType || file.type || 'application/octet-stream',
      },
    });
  }

  async confirmUpload(documentId: string): Promise<GlobalDocument> {
    const response = await apiClient.post(`/global-knowledge/documents/${documentId}/confirm`);
    return response.data.data;
  }

  async deleteDocument(id: string): Promise<void> {
    await apiClient.delete(`/global-knowledge/documents/${id}`);
  }

  async reprocessDocument(id: string): Promise<{ success: boolean; chunks?: number; mainTopic?: string; subTopics?: string[]; error?: string }> {
    const response = await apiClient.post(`/global-knowledge/documents/${id}/process`);
    return response.data;
  }

  async uploadATODocument(filename: string, content: string): Promise<ATOUploadResult> {
    const response = await apiClient.post('/global-knowledge/documents/upload-ato', {
      filename,
      content,
    });
    return response.data.data;
  }

  async getDownloadUrl(id: string): Promise<{ downloadUrl: string; filename: string; expiresIn: number }> {
    const response = await apiClient.get(`/global-knowledge/documents/${id}/download`);
    return response.data.data;
  }

  // Tenant access management
  async getTenantAccess(categoryId: string): Promise<{
    categoryId: string;
    tenants: Array<{ tenantId: string; tenantName: string; hasAccess: boolean }>;
  }> {
    const response = await apiClient.get(`/global-knowledge/categories/${categoryId}/tenants`);
    return response.data.data;
  }

  async setTenantAccess(
    categoryId: string,
    tenantId: string,
    hasAccess: boolean
  ): Promise<void> {
    await apiClient.post(`/global-knowledge/categories/${categoryId}/tenants`, {
      tenantId,
      hasAccess,
    });
  }

  async bulkSetTenantAccess(
    categoryId: string,
    tenantIds: string[],
    hasAccess: boolean
  ): Promise<void> {
    await apiClient.post(`/global-knowledge/categories/${categoryId}/tenants/bulk`, {
      tenantIds,
      hasAccess,
    });
  }

  // Tenant domain access management
  async getTenantDomainAccess(tenantId: string): Promise<{
    tenantId: string;
    domains: Array<{
      domainId: string;
      domainName: string;
      domainSlug: string;
      description?: string;
      icon?: string;
      categoryCount: number;
      hasAccess: boolean;
    }>;
  }> {
    const response = await apiClient.get(`/global-knowledge/tenants/${tenantId}/domains`);
    return response.data.data;
  }

  async setTenantDomainAccess(
    tenantId: string,
    domainId: string,
    hasAccess: boolean
  ): Promise<void> {
    await apiClient.post(`/global-knowledge/tenants/${tenantId}/domains`, {
      domainId,
      hasAccess,
    });
  }

  /**
   * Delete all global documents (optionally filtered by categoryId)
   */
  async deleteAllDocuments(categoryId?: string): Promise<{ deletedCount: number }> {
    const params = categoryId ? `?categoryId=${categoryId}` : '';
    const response = await apiClient.delete(`/global-knowledge/documents${params}`);
    return response.data;
  }
}

export const globalKnowledgeService = new GlobalKnowledgeService();
